// Lager 2.1 — refund/cancel delad plan-builder + executor (D9-villkor 1).
//
// EN modul konsumeras av BÅDE `BookingDetailPanel` (privat + legacy
// open-match via `BookingRefundCancelDialog`) och `ActivityDetailModal`
// (canonical open-match, 2.3 plug-in). Designad så att 2.2 (per_rules)
// och 2.3 (open_match_all) kan plugga in i samma plan-union utan
// omskrivning — bara nya grenar i resolver-switch + executor-switch.
//
// Pattern (3b.1-precedens):
//   - resolveRefundCancelPlan: PURE — ingen Supabase/React/Date.now
//   - executeRefundCancelPlan: tunn executor som tar emot SupabaseClient
//   - caller (dialog) wrappar executor i useMutation för React-Query-
//     invalidation och toast-handling
//
// Atomicitet (Fas 1 §8 godkänd): refund → cancel → audit, refund non-fatal,
// cancel FATAL, audit fire-and-forget. Halv-state (refund-success +
// cancel-fail) returneras explicit i `cancelError` + `halfStateWarning`
// så caller kan visa tydlig toast.
//
// P2-källa: `booking_payments.amount_minor` (öre, canonical). Resolvern
// kräver `amountMinor: number` på input — caller mappar legacy
// `Math.round(amount * 100)` fallback om amount_minor är null.
//
// F19.2/F19.3 (gratis/0%) — resolvern auto-degraderar full_override till
// cancel_no_refund om paid_payments är tom; per_rules degraderar
// motsvarande för pct=0 (upgraderar till full_override för pct=100).

import type { SupabaseClient } from "@supabase/supabase-js";
import type { CancellationTier } from "./cancellationFee";

// ─── Plan-union (discriminated, forward-compatible) ─────────────────────────

export type RefundMode =
  | "full_override"      // 2.1 active — refund exakt debiterat (P2)
  | "per_rules"          // 2.2 (forward-compat — disabled i 2.1)
  | "cancel_no_refund"   // F19.2/F19.3 — gratis ELLER tier 0% ELLER admin explicit
  | "open_match_all";    // 2.3 (forward-compat — D6, throw early i 2.1)

export type AuditAction =
  | "admin_cancelled"             // cancel_no_refund (gratis eller 0% eller admin no-refund)
  | "admin_refunded_full"         // full_override (2.1)
  | "admin_refunded_per_rules"    // per_rules (2.2)
  | "admin_open_match_cancelled"; // open_match_all (2.3)

// Minsta-shape från booking_payments. Caller mappar Tables<"booking_payments">.
export interface RefundablePayment {
  paymentId: string;
  status: string;                       // 'paid' | 'manual' | 'refunded' | 'pending' | ...
  amountMinor: number;                  // öre — P2 source-of-truth (krävs; caller hanterar fallback)
  stripePaymentIntentId: string | null; // null = manual/cash payment
  userId: string;
}

export interface RefundCancelPlan {
  bookingId: string;
  mode: RefundMode;
  /** Per payment-rad: vad ska refunderas (öre). Tom array = cancel utan refund. */
  refunds: Array<{
    paymentId: string;
    stripeIntentId: string | null;
    /** Öre-exakt belopp att refundera. För full_override = originalMinor.
     *  För per_rules = round(originalMinor × rulesRefundPercent / 100). */
    refundMinor: number;
    /** För refund-payment-call: partial=true IFF (0 < refundMinor < originalMinor) */
    isPartial: boolean;
  }>;
  auditAction: AuditAction;
  /** Total förväntad refund (öre) — för UI-visning innan submit (Lesson 23) */
  expectedRefundTotalMinor: number;
  /** För open_match_all (2.3); ej använd i 2.1 (alltid undefined) */
  openMatchCancel?: {
    openMatchId: string;
    participantUserIds: string[];
  };
  /**
   * Lager 2.2 — för per_rules-mode: tier-spårbarhet för audit
   * (varför blev refunden X%). Sätts av caller (dialog) när tier-resolver
   * har körts. Bevaras EJ vid auto-degrade till full_override eller
   * cancel_no_refund (Decision 10.5 — ren degraded plan).
   */
  perRulesMeta?: {
    matchedTier: CancellationTier;
    refundPercent: number;
    hoursLeft: number;
    tierLabel: string;
  };
}

// ─── Pure resolver ──────────────────────────────────────────────────────────

export interface ResolveRefundCancelPlanArgs {
  bookingId: string;
  /** Alla booking_payments för bokningen. Resolver filtrerar paid/manual själv. */
  payments: RefundablePayment[];
  /** Mode operatören valt i dialogen */
  mode: RefundMode;
  /** Krävs IFF mode='per_rules' (2.2). 0 = ingen refund, 100 = full. Annars ignoreras. */
  rulesRefundPercent?: number;
  /**
   * Lager 2.2 — tier-spårbarhet att inkludera i plan.perRulesMeta.
   * Bevaras BARA om mode förblir 'per_rules' (inte vid degrade till
   * full_override/cancel_no_refund — Decision 10.5).
   */
  perRulesMeta?: RefundCancelPlan["perRulesMeta"];
  /** Krävs IFF mode='open_match_all' (2.3). Annars ignoreras. */
  openMatch?: { id: string; participantUserIds: string[] };
}

/** Filter: payment-statusar som faktiskt har pengar att refundera */
const REFUNDABLE_STATUSES = new Set(["paid", "manual"]);

/**
 * Build a refund/cancel plan. PURE — no Supabase, no React, no Date.
 *
 * Auto-degrade rules (Fas 1 §5):
 *  - mode='full_override' AND paid.length === 0 → cancel_no_refund
 *  - mode='per_rules' AND rulesRefundPercent === 0 → cancel_no_refund
 *  - mode='per_rules' AND rulesRefundPercent === 100 → full_override
 *  - mode='per_rules' AND rulesRefundPercent === undefined → throw
 *  - mode='open_match_all' → throw in 2.1 (per Decision 10.1)
 */
export function resolveRefundCancelPlan(
  args: ResolveRefundCancelPlanArgs,
): RefundCancelPlan {
  const { bookingId, payments, mode, rulesRefundPercent } = args;
  // openMatch reserverat för Lager 2.3 (D6 — open_match_all-mode);
  // ignoreras i 2.1 (open_match_all kastar tidigt nedan).
  void args.openMatch;

  // Decision 10.1: open_match_all kastar tydligt i 2.1 (inte tyst stub)
  if (mode === "open_match_all") {
    throw new Error(
      "open_match_all not built until Lager 2.3 — use Lager 2.1 modes (full_override, cancel_no_refund)",
    );
  }

  const paid = payments.filter((p) => REFUNDABLE_STATUSES.has(p.status));

  // ── cancel_no_refund (explicit eller auto-degraderad) ──
  if (mode === "cancel_no_refund") {
    return {
      bookingId,
      mode: "cancel_no_refund",
      refunds: [],
      auditAction: "admin_cancelled",
      expectedRefundTotalMinor: 0,
    };
  }

  // ── full_override (2.1 active path) ──
  if (mode === "full_override") {
    // Auto-degrade: gratis-bokning (F19.3) → cancel_no_refund
    if (paid.length === 0) {
      return {
        bookingId,
        mode: "cancel_no_refund",
        refunds: [],
        auditAction: "admin_cancelled",
        expectedRefundTotalMinor: 0,
      };
    }
    const refunds = paid.map((p) => ({
      paymentId: p.paymentId,
      stripeIntentId: p.stripePaymentIntentId,
      refundMinor: p.amountMinor,
      isPartial: false, // full refund — refund-payment skickas utan partial-flag
    }));
    const total = refunds.reduce((s, r) => s + r.refundMinor, 0);
    return {
      bookingId,
      mode: "full_override",
      refunds,
      auditAction: "admin_refunded_full",
      expectedRefundTotalMinor: total,
    };
  }

  // ── per_rules (2.2 — forward-compat, validation only i 2.1) ──
  if (mode === "per_rules") {
    if (typeof rulesRefundPercent !== "number" || !Number.isFinite(rulesRefundPercent)) {
      throw new Error("rulesRefundPercent required for mode='per_rules'");
    }
    if (rulesRefundPercent < 0 || rulesRefundPercent > 100) {
      throw new Error(
        `rulesRefundPercent must be in [0, 100] (got ${rulesRefundPercent})`,
      );
    }
    // Auto-degrade: 0% → cancel_no_refund (F19.2)
    if (rulesRefundPercent === 0) {
      return {
        bookingId,
        mode: "cancel_no_refund",
        refunds: [],
        auditAction: "admin_cancelled",
        expectedRefundTotalMinor: 0,
      };
    }
    // Auto-upgrade: 100% → full_override (same outcome, simpler executor branch)
    if (rulesRefundPercent === 100) {
      return resolveRefundCancelPlan({ bookingId, payments, mode: "full_override" });
    }
    // 0 < pct < 100: partial refund per payment
    if (paid.length === 0) {
      // Gratis-bokning med per_rules: degrade till cancel_no_refund
      return {
        bookingId,
        mode: "cancel_no_refund",
        refunds: [],
        auditAction: "admin_cancelled",
        expectedRefundTotalMinor: 0,
      };
    }
    const refunds = paid.map((p) => ({
      paymentId: p.paymentId,
      stripeIntentId: p.stripePaymentIntentId,
      refundMinor: Math.round((p.amountMinor * rulesRefundPercent) / 100),
      isPartial: true,
    }));
    const total = refunds.reduce((s, r) => s + r.refundMinor, 0);
    return {
      bookingId,
      mode: "per_rules",
      refunds,
      auditAction: "admin_refunded_per_rules",
      expectedRefundTotalMinor: total,
      // Lager 2.2: pass-through tier-spårbarhet för audit
      ...(args.perRulesMeta ? { perRulesMeta: args.perRulesMeta } : {}),
    };
  }

  // Unreachable per TypeScript exhaustiveness; defensive throw.
  const _exhaustive: never = mode;
  throw new Error(`Unknown RefundMode: ${String(_exhaustive)}`);
}

// ─── Executor (tunn — Supabase + edge fn + audit) ───────────────────────────

export interface ExecuteRefundCancelPlanDeps {
  supabase: SupabaseClient;
  adminUserId: string;
  reason: string;
}

export interface ExecuteRefundCancelPlanResult {
  /** false IFF cancel-UPDATE failade (FATAL). */
  success: boolean;
  refundedCount: number;
  refundedAmountMinor: number;
  /** Per-payment non-fatal refund-fel (admin retry separat). */
  failedRefunds: Array<{ paymentId: string; error: string }>;
  /** Fatal cancel-error om success=false. */
  cancelError?: string;
  /** Status om refund-fail kom EFTER cancel — halv-state varning. */
  halfStateWarning?: string;
}

/**
 * Execute a RefundCancelPlan. Ordning: refund (non-fatal) → cancel (FATAL)
 * → audit (fire-and-forget). Returnerar resultat utan att throwa — caller
 * (dialog) använder result.success för toast-handling.
 *
 * Atomicitet (Fas 1 §8):
 * - Refund-loop kontinuerar trots fel; failures samlas i failedRefunds[]
 * - Cancel UPDATE bookings.status='cancelled': om FAIL → halt + return success=false
 * - Audit-INSERT fire-and-forget (.then catch) — påverkar inte success
 * - Dubbelklick-idempotens: paidPayments redan filtreras till 'paid'|'manual';
 *   refunded payments hoppas över naturligt vid retry
 */
export async function executeRefundCancelPlan(
  plan: RefundCancelPlan,
  deps: ExecuteRefundCancelPlanDeps,
): Promise<ExecuteRefundCancelPlanResult> {
  const result: ExecuteRefundCancelPlanResult = {
    success: false,
    refundedCount: 0,
    refundedAmountMinor: 0,
    failedRefunds: [],
  };

  // ── 1. Refund-loop (non-fatal per payment) ───────────────────────────────
  for (const r of plan.refunds) {
    try {
      if (r.stripeIntentId !== null) {
        // Stripe path via refund-payment v17 edge fn
        const { data, error } = await deps.supabase.functions.invoke(
          "refund-payment",
          {
            body: {
              payment_id: r.paymentId,
              reason: deps.reason || "Admin avbokade",
              ...(r.isPartial ? { partial: true, amount: r.refundMinor } : {}),
            },
          },
        );
        if (error) {
          result.failedRefunds.push({
            paymentId: r.paymentId,
            error: error.message ?? String(error),
          });
        } else if (data && typeof data === "object" && "error" in data && data.error) {
          // Edge fn may return 200 with error in body (e.g. validation)
          result.failedRefunds.push({
            paymentId: r.paymentId,
            error: String((data as { error: unknown }).error),
          });
        } else {
          result.refundedCount += 1;
          result.refundedAmountMinor += r.refundMinor;
        }
      } else {
        // Manual/cash refund — direkt UPDATE (matchar nuvarande dialog-mönster).
        // refund-payment edge fn skulle ge 400 för partial cash; för full ger
        // den UPDATE — vi gör samma direkt för att slippa edge-fn-overhead.
        if (r.isPartial) {
          result.failedRefunds.push({
            paymentId: r.paymentId,
            error: "Partial refunds require a Stripe payment intent",
          });
          continue;
        }
        const { error } = await deps.supabase
          .from("booking_payments")
          .update({ status: "refunded", paid_at: null } as never)
          .eq("id", r.paymentId);
        if (error) {
          result.failedRefunds.push({
            paymentId: r.paymentId,
            error: error.message,
          });
        } else {
          result.refundedCount += 1;
          result.refundedAmountMinor += r.refundMinor;
        }
      }
    } catch (err) {
      result.failedRefunds.push({
        paymentId: r.paymentId,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  // ── 2. Cancel booking (FATAL om fail) ────────────────────────────────────
  const { error: cancelErr } = await deps.supabase
    .from("bookings")
    .update({ status: "cancelled", cancelled_by: deps.adminUserId } as never)
    .eq("id", plan.bookingId);

  if (cancelErr) {
    result.cancelError = cancelErr.message;
    if (result.refundedCount > 0) {
      result.halfStateWarning =
        `Återbetalning lyckades (${result.refundedCount} st, ${result.refundedAmountMinor / 100} kr) men avbokning misslyckades. ` +
        `Försök igen eller kontakta support — referens: ${plan.bookingId.slice(0, 8)}`;
    }
    return result;
  }

  // ── 3. Audit-log (fire-and-forget) ───────────────────────────────────────
  const description = buildAuditDescription(plan, result, deps.reason);
  deps.supabase
    .from("booking_activity_log" as never)
    .insert({
      booking_id: plan.bookingId,
      user_id: deps.adminUserId,
      action: plan.auditAction,
      actor_type: "admin",
      description,
      metadata: {
        mode: plan.mode,
        expected_refund_total_minor: plan.expectedRefundTotalMinor,
        refunded_count: result.refundedCount,
        refunded_amount_minor: result.refundedAmountMinor,
        failed_refunds: result.failedRefunds,
        reason: deps.reason || null,
        // Lager 2.2: tier-spårbarhet (varför blev refunden X%)
        ...(plan.perRulesMeta ? { per_rules_meta: plan.perRulesMeta } : {}),
      },
    } as never)
    .then(
      () => {},
      (err: unknown) => {
        // Lesson 26-spår: audit-fail logas men blockar inte. Bokning är
        // redan cancelled i DB, refunds är gjorda — historiken kan
        // rekonstrueras via bookings + booking_payments själva.
        console.error("[refundCancelPlan] audit-log INSERT failed:", err);
      },
    );

  result.success = true;
  return result;
}

// ─── Internals ──────────────────────────────────────────────────────────────

function buildAuditDescription(
  plan: RefundCancelPlan,
  result: ExecuteRefundCancelPlanResult,
  reason: string,
): string {
  const reasonSuffix = reason ? `: ${reason}` : "";
  const failSuffix =
    result.failedRefunds.length > 0
      ? ` (${result.failedRefunds.length} refund-fel — manuell uppföljning)`
      : "";
  switch (plan.auditAction) {
    case "admin_cancelled":
      return `Avbokad av admin${reasonSuffix}${failSuffix}`;
    case "admin_refunded_full": {
      const kr = Math.round(plan.expectedRefundTotalMinor / 100);
      return `Avbokad + full återbetalning (${kr} kr)${reasonSuffix}${failSuffix}`;
    }
    case "admin_refunded_per_rules": {
      const kr = Math.round(plan.expectedRefundTotalMinor / 100);
      // pct härleds via expectedRefundTotalMinor / paid.amountMinor men plan-shape
      // bevarar inte pct direkt — visa belopp + flagga "enligt regler"
      return `Avbokad + återbetalning enligt regler (${kr} kr)${reasonSuffix}${failSuffix}`;
    }
    case "admin_open_match_cancelled":
      return `Match avbokad + alla deltagare återbetalda${reasonSuffix}${failSuffix}`;
  }
}
